// transport — Play/Pause button + current/duration time display.
//
// Uses real desktop class names (.tr-play / .tr-curr / .tr-total) with
// the .rh-tr-timeline wrapper, mirroring app/renderer/ui/player-panel.js.
// CSS extracted to app/renderer/ui/rehearsal/transport.css (consumed via
// the @desktop-css alias).
//
// Subscribes to app state for playing/loaded/manifest/playhead. Click toggles
// player.play()/pause(). Current-time display updates via RAF while playing
// (and once-per-state-change while paused, so seeks reflect immediately).

import {
  getState as defaultGetState,
  subscribe as defaultSubscribe,
  type AppState,
} from '../state.js';
import type { Player } from '../player.js';

export interface MountOpts {
  player: Player;
  stateSource?: {
    subscribe: typeof defaultSubscribe;
    getState: typeof defaultGetState;
  };
  raf?: (cb: FrameRequestCallback) => number;
  cancelRaf?: (id: number) => void;
}

export interface TransportHandle {
  unmount: () => void;
}

// SVG path data for play (▶) and pause (⏸) glyphs, sized to a 24×24 viewBox.
const PLAY_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
const PAUSE_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>';

export function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const total = Math.floor(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function mountTransport(host: Element, opts: MountOpts): TransportHandle {
  const subscribe = opts.stateSource?.subscribe ?? defaultSubscribe;
  const getState = opts.stateSource?.getState ?? defaultGetState;
  const requestFrame = opts.raf ?? ((cb) => requestAnimationFrame(cb));
  const cancelFrame = opts.cancelRaf ?? ((id) => cancelAnimationFrame(id));
  const { player } = opts;

  let unmounted = false;

  // Container is the host itself — caller styles `.rh-transport` on it if desired.
  const playBtn = document.createElement('button');
  playBtn.className = 'tr-play';
  playBtn.setAttribute('aria-label', 'Play');
  playBtn.innerHTML = PLAY_SVG;

  // Timeline wrapper mirrors desktop's `.rh-tr-timeline` (player-panel.js).
  // Separator between current and total is an inline text node — desktop has
  // no `.rh-transport-time-sep` analog at this layer.
  const timeWrap = document.createElement('div');
  timeWrap.className = 'rh-tr-timeline';
  const timeCur = document.createElement('span');
  timeCur.className = 'tr-curr';
  timeCur.textContent = '0:00';
  const timeSep = document.createTextNode(' / ');
  const timeDur = document.createElement('span');
  timeDur.className = 'tr-total';
  timeDur.textContent = '0:00';
  timeWrap.appendChild(timeCur);
  timeWrap.appendChild(timeSep);
  timeWrap.appendChild(timeDur);

  host.appendChild(playBtn);
  host.appendChild(timeWrap);

  let isPlaying = false;
  let isLoaded = false;
  let lastDuration = 0;
  let rafId: number | null = null;
  let lastCurText = '0:00';

  // Mounted disabled by default — `onState` will enable once state.loaded flips.
  setDisabled(true);

  function setIcon(playing: boolean): void {
    playBtn.innerHTML = playing ? PAUSE_SVG : PLAY_SVG;
    playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
  }

  function setDisabled(disabled: boolean): void {
    if (disabled) {
      playBtn.setAttribute('aria-disabled', 'true');
      playBtn.disabled = true;
    } else {
      playBtn.removeAttribute('aria-disabled');
      playBtn.disabled = false;
    }
  }

  function updateCur(sec: number): void {
    const text = formatTime(sec);
    if (text !== lastCurText) {
      lastCurText = text;
      timeCur.textContent = text;
    }
  }

  function rafTick(): void {
    if (unmounted) return;
    updateCur(player.getPlayhead());
    if (isPlaying) {
      rafId = requestFrame(rafTick);
    } else {
      rafId = null;
    }
  }

  function startRaf(): void {
    if (rafId !== null) return;
    rafId = requestFrame(rafTick);
  }

  function stopRaf(): void {
    if (rafId !== null) {
      cancelFrame(rafId);
      rafId = null;
    }
  }

  function onClick(): void {
    if (unmounted) return;
    if (!isLoaded) return;
    if (player.isPlaying()) {
      player.pause();
    } else {
      // Fire and forget — play() returns a Promise but transport only cares
      // about state-driven UI updates.
      void player.play();
    }
  }
  playBtn.addEventListener('click', onClick);

  // Global Space → toggle play/pause (universal media-player convention).
  // Bails when an editable element holds focus so typing isn't hijacked.
  // preventDefault stops the browser's default behavior (page scroll + the
  // focused button's space-activation) so we don't fire onClick twice.
  function onKeyDown(ev: KeyboardEvent): void {
    if (unmounted) return;
    if (ev.code !== 'Space' && ev.key !== ' ') return;
    const target = ev.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }
    }
    ev.preventDefault();
    onClick();
  }
  document.addEventListener('keydown', onKeyDown);

  function onState(state: AppState): void {
    if (unmounted) return;

    if (state.manifest) {
      const d = state.manifest.duration;
      if (d !== lastDuration) {
        lastDuration = d;
        timeDur.textContent = formatTime(d);
      }
    } else if (lastDuration !== 0) {
      lastDuration = 0;
      timeDur.textContent = '0:00';
    }

    if (state.loaded !== isLoaded) {
      isLoaded = state.loaded;
      setDisabled(!isLoaded);
    }

    // Always reflect current state.playhead so seek-while-paused shows up.
    updateCur(state.playhead);

    if (state.playing && !isPlaying) {
      isPlaying = true;
      setIcon(true);
      startRaf();
    } else if (!state.playing && isPlaying) {
      isPlaying = false;
      setIcon(false);
      stopRaf();
    }
  }

  const unsubscribe = subscribe(onState);

  return {
    unmount: () => {
      if (unmounted) return;
      unmounted = true;
      stopRaf();
      unsubscribe();
      playBtn.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
      if (playBtn.parentNode === host) host.removeChild(playBtn);
      if (timeWrap.parentNode === host) host.removeChild(timeWrap);
    },
  };
}
